import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Heading,
  IconButton,
  Input,
  Link,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { ArrowLeft, Check, Copy, Edit, ExternalLink, Mail, Plus, Star, Trash2 } from "lucide-react";

const CONTACT_SOURCES = ["apollo", "clienthub", "website", "google_places", "linkedin", "hunter", "clay", "rocketreach", "manual", "other"];
const EMAIL_STATUSES = ["unknown", "verified", "likely", "risky", "invalid"];
const CONTACT_QUALITIES = ["strong", "medium", "weak", "unknown"];
const DEFAULT_EMAIL_SUBJECT = "Jobber schedule openings";
const SALES_STATUSES = [
  "uncontacted",
  "researching",
  "ready_to_contact",
  "contacted",
  "replied",
  "interested",
  "call_scheduled",
  "trial",
  "customer",
  "not_interested",
  "no_response",
  "bad_fit",
];

function titleize(value) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return "Not set";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
}

function nowLocal() {
  return toDateTimeLocal(new Date().toISOString());
}

function normalizedHttpUrl(value) {
  if (!value) return null;
  const rawValue = String(value).trim();
  if (!rawValue) return null;

  try {
    const url = new URL(rawValue.startsWith("http://") || rawValue.startsWith("https://") ? rawValue : `https://${rawValue}`);
    return ["http:", "https:"].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
}

function displayUrl(value) {
  const normalizedUrl = normalizedHttpUrl(value);
  if (!normalizedUrl) return String(value || "");
  return normalizedUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function contactBadgeColor(quality) {
  if (quality === "strong") return "green";
  if (quality === "medium") return "blue";
  if (quality === "weak") return "orange";
  return "gray";
}

function salesStatusColor(status) {
  if (["interested", "call_scheduled", "trial", "customer"].includes(status)) return "green";
  if (["contacted", "replied", "ready_to_contact", "researching"].includes(status)) return "blue";
  if (["not_interested", "bad_fit"].includes(status)) return "red";
  if (status === "no_response") return "orange";
  return "gray";
}

function contactFirstName(contact) {
  const name = contact?.first_name || contact?.full_name || contact?.display_name || "";
  const firstName = String(name).trim().split(/\s+/)[0];
  return firstName || "there";
}

function buildDefaultEmailBody(lead, contact) {
  const firstName = contactFirstName(contact);
  const company = lead?.company_name || "your company";

  return `Hi ${firstName},

I saw that ${company} uses Jobber. Quick question: when you get a last-minute schedule opening, how do you usually fill it?

I built Same Day Openings, a Jobber Marketplace app that automatically offers those openings to customers who want to get in sooner.

If this is something your team deals with, I’d be happy to show you how it works. It’s free for 30 days and doesn’t require a card.

Thanks,
Jackson
Founder, Same Day Openings
samedayopenings.com`;
}

function isDefaultEmailBody(body, lead, contacts) {
  if (!body) return true;
  return [undefined, ...contacts].some((contact) => body === buildDefaultEmailBody(lead, contact));
}

const emptyContact = {
  first_name: "",
  last_name: "",
  full_name: "",
  job_title: "",
  email: "",
  phone: "",
  linkedin_url: "",
  contact_source: "manual",
  contact_source_details: "",
  contact_source_url: "",
  email_verification_status: "unknown",
  contact_quality: "unknown",
  is_primary: false,
  notes: "",
};

const emptyEmail = {
  lead_contact_id: "",
  interaction_type: "email",
  direction: "outbound",
  channel: "email",
  recipient_email: "",
  subject: DEFAULT_EMAIL_SUBJECT,
  body: "",
  occurred_at: nowLocal(),
  outreach_sequence_position: "1",
  notes: "",
};

export default function LeadDetail() {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [contactForm, setContactForm] = useState(emptyContact);
  const [editingContactId, setEditingContactId] = useState(null);
  const [emailForm, setEmailForm] = useState(emptyEmail);
  const [noteBody, setNoteBody] = useState("");
  const [expandedInteractions, setExpandedInteractions] = useState({});
  const toast = useToast();

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  const contacts = lead?.lead_contacts || [];
  const interactions = lead?.lead_interactions || [];
  const primaryContact = contacts.find((contact) => contact.is_primary);
  const otherContacts = contacts.filter((contact) => !contact.is_primary);
  const websiteUrl = normalizedHttpUrl(lead?.website_url);

  const nextSequencePosition = useMemo(() => {
    const outboundEmails = interactions.filter((interaction) => interaction.interaction_type === "email" && interaction.direction === "outbound");
    const maxPosition = Math.max(0, ...outboundEmails.map((interaction) => Number(interaction.outreach_sequence_position || 0)));
    return String(maxPosition + 1);
  }, [interactions]);

  const loadLead = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/leads/${leadId}`, {
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Failed to load lead");

      setLead(data.lead);
      setEmailForm((current) => ({
        ...current,
        outreach_sequence_position: current.outreach_sequence_position || nextSequencePosition,
      }));
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLead();
  }, [leadId]);

  useEffect(() => {
    if (!lead) return;
    const selectedContact = contacts.find((contact) => String(contact.id) === String(emailForm.lead_contact_id)) || primaryContact;
    setEmailForm((current) => ({
      ...current,
      recipient_email: current.recipient_email || lead.best_outreach_email || "",
      subject: current.subject || DEFAULT_EMAIL_SUBJECT,
      body: isDefaultEmailBody(current.body, lead, contacts) ? buildDefaultEmailBody(lead, selectedContact) : current.body,
      outreach_sequence_position: current.outreach_sequence_position || nextSequencePosition,
    }));
  }, [lead, nextSequencePosition]);

  const apiRequest = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        ...(options.headers || {}),
      },
    });
    const data = await response.json();
    if (!response.ok || !data.success) throw new Error(data.error || "Request failed");
    return data;
  };

  const updateLeadField = async (field, value) => {
    try {
      setSaving(true);
      const data = await apiRequest(`/api/leads/${leadId}`, {
        method: "PATCH",
        body: JSON.stringify({ lead: { [field]: value || null } }),
      });
      setLead(data.lead);
    } catch (err) {
      toast({ title: "Failed to update lead", description: err.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const resetContactForm = () => {
    setEditingContactId(null);
    setContactForm(emptyContact);
  };

  const saveContact = async () => {
    try {
      setSaving(true);
      const url = editingContactId ? `/api/leads/${leadId}/contacts/${editingContactId}` : `/api/leads/${leadId}/contacts`;
      const data = await apiRequest(url, {
        method: editingContactId ? "PATCH" : "POST",
        body: JSON.stringify({ lead_contact: contactForm }),
      });
      await loadLead();
      resetContactForm();
      toast({ title: editingContactId ? "Contact updated" : "Contact added", status: "success", duration: 2500, isClosable: true });
      return data;
    } catch (err) {
      toast({ title: "Failed to save contact", description: err.message, status: "error", duration: 5000, isClosable: true });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const editContact = (contact) => {
    setEditingContactId(contact.id);
    setContactForm({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      full_name: contact.full_name || "",
      job_title: contact.job_title || "",
      email: contact.email || "",
      phone: contact.phone || "",
      linkedin_url: contact.linkedin_url || "",
      contact_source: contact.contact_source || "manual",
      contact_source_details: contact.contact_source_details || "",
      contact_source_url: contact.contact_source_url || "",
      email_verification_status: contact.email_verification_status || "unknown",
      contact_quality: contact.contact_quality || "unknown",
      is_primary: !!contact.is_primary,
      notes: contact.notes || "",
    });
  };

  const deleteContact = async (contact) => {
    if (!window.confirm(`Delete ${contact.display_name}?`)) return;
    try {
      setSaving(true);
      await apiRequest(`/api/leads/${leadId}/contacts/${contact.id}`, { method: "DELETE" });
      await loadLead();
      toast({ title: "Contact deleted", status: "success", duration: 2500, isClosable: true });
    } catch (err) {
      toast({ title: "Failed to delete contact", description: err.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const makePrimary = async (contact) => {
    try {
      setSaving(true);
      await apiRequest(`/api/leads/${leadId}/contacts/${contact.id}/make_primary`, { method: "POST" });
      await loadLead();
      toast({ title: "Primary contact updated", status: "success", duration: 2500, isClosable: true });
    } catch (err) {
      toast({ title: "Failed to update primary contact", description: err.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const selectEmailContact = (contactId) => {
    const contact = contacts.find((item) => String(item.id) === String(contactId));
    setEmailForm((current) => ({
      ...current,
      lead_contact_id: contactId,
      recipient_email: contact?.email || lead?.email || "",
      subject: current.subject || DEFAULT_EMAIL_SUBJECT,
      body: isDefaultEmailBody(current.body, lead, contacts) ? buildDefaultEmailBody(lead, contact) : current.body,
    }));
  };

  const logEmail = async () => {
    try {
      setSaving(true);
      const payload = {
        ...emailForm,
        lead_contact_id: emailForm.lead_contact_id || null,
        occurred_at: emailForm.occurred_at || nowLocal(),
        outreach_sequence_position: emailForm.outreach_sequence_position || null,
      };
      const data = await apiRequest(`/api/leads/${leadId}/interactions`, {
        method: "POST",
        body: JSON.stringify({ lead_interaction: payload }),
      });
      setLead(data.lead);
      const nextPrimaryContact = (data.lead.lead_contacts || []).find((contact) => contact.is_primary);
      setEmailForm({
        ...emptyEmail,
        recipient_email: data.lead.best_outreach_email || "",
        subject: DEFAULT_EMAIL_SUBJECT,
        body: buildDefaultEmailBody(data.lead, nextPrimaryContact),
        occurred_at: nowLocal(),
        outreach_sequence_position: String(Number(payload.outreach_sequence_position || nextSequencePosition) + 1),
      });
      toast({ title: "Email logged", status: "success", duration: 2500, isClosable: true });
    } catch (err) {
      toast({ title: "Failed to log email", description: err.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try {
      setSaving(true);
      const data = await apiRequest(`/api/leads/${leadId}/interactions`, {
        method: "POST",
        body: JSON.stringify({
          lead_interaction: {
            interaction_type: "note",
            direction: "internal",
            channel: "other",
            body: noteBody,
            occurred_at: nowLocal(),
          },
        }),
      });
      setLead(data.lead);
      setNoteBody("");
      toast({ title: "Note added", status: "success", duration: 2500, isClosable: true });
    } catch (err) {
      toast({ title: "Failed to add note", description: err.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (label, text) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    toast({ title: `${label} copied`, status: "success", duration: 1600, isClosable: true });
  };

  if (loading && !lead) {
    return (
      <Box maxW="1400px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
        <VStack spacing={3}>
          <Spinner color="blue.500" />
          <Text color="gray.600">Loading lead...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxW="1400px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
        <Alert status="error"><AlertIcon />{error}</Alert>
      </Box>
    );
  }

  return (
    <Box maxW="1400px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <VStack align="stretch" spacing={5}>
        <HStack justify="space-between" align="start">
          <Box>
            <Button as={RouterLink} to="/leads" variant="ghost" size="sm" leftIcon={<ArrowLeft size={16} />} mb={3}>
              Leads
            </Button>
            <Heading size="lg">{lead.company_name}</Heading>
            <HStack mt={2} spacing={2} wrap="wrap">
              <Badge colorScheme={lead.lead_tier === "A" ? "green" : lead.lead_tier === "B" ? "blue" : "gray"}>{lead.lead_tier ? `Tier ${lead.lead_tier}` : "Unscored"}</Badge>
              <Badge colorScheme={salesStatusColor(lead.sales_status)}>{titleize(lead.sales_status)}</Badge>
              {lead.jobber_mentioned && <Badge colorScheme="green">Jobber Confirmed</Badge>}
              {lead.follow_up_overdue && <Badge colorScheme="red">Follow-up overdue</Badge>}
            </HStack>
          </Box>
          {saving && <Spinner color="blue.500" />}
        </HStack>

        <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap={5}>
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <Heading size="sm" mb={3}>Company</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <Box>
                <Text fontSize="xs" color="gray.600" fontWeight="semibold">Lead score</Text>
                <Text>{lead.lead_score ?? "Unknown"}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.600" fontWeight="semibold">Industry</Text>
                <Text>{lead.industry ? titleize(lead.industry) : "Unknown"}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.600" fontWeight="semibold">Website</Text>
                {websiteUrl ? <Link href={websiteUrl} color="blue.600" isExternal>{displayUrl(websiteUrl)} <ExternalLink size={12} style={{ display: "inline" }} /></Link> : <Text>Unknown</Text>}
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.600" fontWeight="semibold">Company fallback email</Text>
                {lead.email ? <Link href={`mailto:${lead.email}`} color="blue.600">{lead.email}</Link> : <Text>None</Text>}
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.600" fontWeight="semibold">Field staff</Text>
                <Text>{lead.field_staff_count_estimate ?? "Unknown"}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.600" fontWeight="semibold">Google reviews</Text>
                <Text>{lead.google_review_count ?? "Unknown"}</Text>
              </Box>
            </SimpleGrid>
          </Box>

          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
            <Heading size="sm" mb={3}>Sales Tracking</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold">Sales status</FormLabel>
                <Select value={lead.sales_status || "uncontacted"} onChange={(event) => updateLeadField("sales_status", event.target.value)}>
                  {SALES_STATUSES.map((status) => <option key={status} value={status}>{titleize(status)}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold">Next follow-up</FormLabel>
                <Input type="datetime-local" value={toDateTimeLocal(lead.next_follow_up_at)} onChange={(event) => updateLeadField("next_follow_up_at", event.target.value)} />
              </FormControl>
            </SimpleGrid>
            <Text mt={3} fontSize="sm" color="gray.600">Last contacted: {formatDateTime(lead.last_contacted_at)}</Text>
          </Box>
        </Grid>

        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <HStack justify="space-between" align="center" mb={3}>
            <Heading size="sm">Contacts</Heading>
            <Text fontSize="sm" color="gray.600">{contacts.length} saved</Text>
          </HStack>

          {primaryContact && (
            <Box border="1px solid" borderColor="green.200" bg="green.50" borderRadius="md" p={3} mb={3}>
              <HStack justify="space-between" align="start">
                <Box>
                  <Badge colorScheme="green" mb={2}>Primary</Badge>
                  <Text fontWeight="semibold">{primaryContact.display_name}</Text>
                  <Text fontSize="sm" color="gray.700">{primaryContact.job_title || "No title"}</Text>
                  <Text fontSize="sm" color="blue.700">{primaryContact.email || "No email"}</Text>
                  <Text fontSize="sm" color="gray.700">Source: {titleize(primaryContact.contact_source)} · Email: {titleize(primaryContact.email_verification_status)} · Quality: {titleize(primaryContact.contact_quality)}</Text>
                </Box>
                <HStack>
                  <IconButton aria-label="Edit contact" icon={<Edit size={16} />} size="sm" variant="ghost" onClick={() => editContact(primaryContact)} />
                  <IconButton aria-label="Delete contact" icon={<Trash2 size={16} />} size="sm" variant="ghost" colorScheme="red" onClick={() => deleteContact(primaryContact)} />
                </HStack>
              </HStack>
            </Box>
          )}

          <Table size="sm" mb={4}>
            <Thead bg="gray.50">
              <Tr>
                <Th>Name</Th>
                <Th>Title</Th>
                <Th>Email</Th>
                <Th>Source</Th>
                <Th>Quality</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {otherContacts.map((contact) => (
                <Tr key={contact.id}>
                  <Td>{contact.display_name}</Td>
                  <Td>{contact.job_title || "Unknown"}</Td>
                  <Td>{contact.email || "None"}</Td>
                  <Td>{titleize(contact.contact_source)}</Td>
                  <Td><Badge colorScheme={contactBadgeColor(contact.contact_quality)}>{titleize(contact.contact_quality)}</Badge></Td>
                  <Td textAlign="right">
                    <HStack justify="flex-end">
                      <IconButton aria-label="Make primary" icon={<Star size={16} />} size="sm" variant="ghost" onClick={() => makePrimary(contact)} />
                      <IconButton aria-label="Edit contact" icon={<Edit size={16} />} size="sm" variant="ghost" onClick={() => editContact(contact)} />
                      <IconButton aria-label="Delete contact" icon={<Trash2 size={16} />} size="sm" variant="ghost" colorScheme="red" onClick={() => deleteContact(contact)} />
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {!primaryContact && otherContacts.length === 0 && (
                <Tr><Td colSpan={6}><Text textAlign="center" color="gray.600" py={4}>No contacts yet.</Text></Td></Tr>
              )}
            </Tbody>
          </Table>

          <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Heading size="xs" mb={3}>{editingContactId ? "Edit contact" : "Add contact"}</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <Input placeholder="Full name" value={contactForm.full_name} onChange={(event) => setContactForm({ ...contactForm, full_name: event.target.value })} />
              <Input placeholder="Title" value={contactForm.job_title} onChange={(event) => setContactForm({ ...contactForm, job_title: event.target.value })} />
              <Input placeholder="Email" value={contactForm.email} onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })} />
              <Select value={contactForm.contact_source} onChange={(event) => setContactForm({ ...contactForm, contact_source: event.target.value })}>
                {CONTACT_SOURCES.map((source) => <option key={source} value={source}>{titleize(source)}</option>)}
              </Select>
              <Select value={contactForm.contact_quality} onChange={(event) => setContactForm({ ...contactForm, contact_quality: event.target.value })}>
                {CONTACT_QUALITIES.map((quality) => <option key={quality} value={quality}>{titleize(quality)}</option>)}
              </Select>
              <Select value={contactForm.email_verification_status} onChange={(event) => setContactForm({ ...contactForm, email_verification_status: event.target.value })}>
                {EMAIL_STATUSES.map((status) => <option key={status} value={status}>{titleize(status)}</option>)}
              </Select>
              <Input placeholder="Phone" value={contactForm.phone} onChange={(event) => setContactForm({ ...contactForm, phone: event.target.value })} />
              <Input placeholder="LinkedIn URL" value={contactForm.linkedin_url} onChange={(event) => setContactForm({ ...contactForm, linkedin_url: event.target.value })} />
              <Input placeholder="Source URL" value={contactForm.contact_source_url} onChange={(event) => setContactForm({ ...contactForm, contact_source_url: event.target.value })} />
            </SimpleGrid>
            <Textarea mt={3} placeholder="Source details or notes" value={contactForm.contact_source_details} onChange={(event) => setContactForm({ ...contactForm, contact_source_details: event.target.value })} />
            <HStack mt={3} justify="space-between">
              <Button size="sm" variant="outline" onClick={resetContactForm}>Clear</Button>
              <Button size="sm" colorScheme="blue" leftIcon={editingContactId ? <Check size={16} /> : <Plus size={16} />} onClick={saveContact} isDisabled={saving}>
                {editingContactId ? "Update contact" : "Add contact"}
              </Button>
            </HStack>
          </Box>
        </Box>

        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <Heading size="sm" mb={3}>Log Email</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold">Contact</FormLabel>
              <Select value={emailForm.lead_contact_id} onChange={(event) => selectEmailContact(event.target.value)}>
                <option value="">Company fallback email</option>
                {contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.display_name}</option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold">Recipient</FormLabel>
              <HStack>
                <Input value={emailForm.recipient_email} onChange={(event) => setEmailForm({ ...emailForm, recipient_email: event.target.value })} />
                <IconButton aria-label="Copy recipient" icon={<Copy size={16} />} onClick={() => copyText("Recipient", emailForm.recipient_email)} />
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold">Sent at</FormLabel>
              <Input type="datetime-local" value={emailForm.occurred_at} onChange={(event) => setEmailForm({ ...emailForm, occurred_at: event.target.value })} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold">Sequence position</FormLabel>
              <Input type="number" min="1" value={emailForm.outreach_sequence_position} onChange={(event) => setEmailForm({ ...emailForm, outreach_sequence_position: event.target.value })} />
            </FormControl>
            <FormControl gridColumn={{ md: "span 2" }}>
              <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold">Subject</FormLabel>
              <HStack>
                <Input value={emailForm.subject} onChange={(event) => setEmailForm({ ...emailForm, subject: event.target.value })} />
                <IconButton aria-label="Copy subject" icon={<Copy size={16} />} onClick={() => copyText("Subject", emailForm.subject)} />
              </HStack>
            </FormControl>
          </SimpleGrid>
          <FormControl mt={3}>
            <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold">Body</FormLabel>
            <Textarea minH="180px" value={emailForm.body} onChange={(event) => setEmailForm({ ...emailForm, body: event.target.value })} />
          </FormControl>
          <HStack mt={3} justify="space-between">
            <Button size="sm" variant="outline" leftIcon={<Copy size={16} />} onClick={() => copyText("Body", emailForm.body)}>Copy body</Button>
            <Button size="sm" colorScheme="blue" leftIcon={<Mail size={16} />} onClick={logEmail} isDisabled={saving}>Log email</Button>
          </HStack>
        </Box>

        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <Heading size="sm" mb={3}>Outreach History</Heading>
          <VStack align="stretch" spacing={3}>
            {interactions.map((interaction) => {
              const expanded = !!expandedInteractions[interaction.id];
              return (
                <Box key={interaction.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                  <HStack justify="space-between" align="start">
                    <Box>
                      <HStack spacing={2} wrap="wrap">
                        <Text fontWeight="semibold">{formatDateTime(interaction.occurred_at)}</Text>
                        <Badge>{titleize(interaction.direction)} {titleize(interaction.interaction_type)}</Badge>
                        {interaction.outreach_sequence_position && <Badge colorScheme="blue">#{interaction.outreach_sequence_position}</Badge>}
                      </HStack>
                      <Text mt={1} color="gray.700">{interaction.lead_contact_name || "Company"} {interaction.recipient_email ? `- ${interaction.recipient_email}` : ""}</Text>
                      {interaction.subject && <Text mt={2} fontWeight="semibold">Subject: {interaction.subject}</Text>}
                    </Box>
                    {interaction.body && (
                      <Button size="xs" variant="ghost" onClick={() => setExpandedInteractions((current) => ({ ...current, [interaction.id]: !expanded }))}>
                        {expanded ? "Hide" : "View"}
                      </Button>
                    )}
                  </HStack>
                  <Collapse in={expanded} animateOpacity>
                    <Box mt={3} bg="gray.50" borderRadius="md" p={3} whiteSpace="pre-wrap" fontSize="sm">
                      {interaction.body}
                    </Box>
                  </Collapse>
                  {interaction.notes && <Text mt={2} fontSize="sm" color="gray.600">{interaction.notes}</Text>}
                </Box>
              );
            })}
            {interactions.length === 0 && <Text color="gray.600">No outreach history yet.</Text>}
          </VStack>
          <Divider my={4} />
          <Heading size="xs" mb={2}>Add internal note</Heading>
          <Textarea value={noteBody} onChange={(event) => setNoteBody(event.target.value)} placeholder="Research note or reply summary" />
          <Button mt={3} size="sm" variant="outline" onClick={addNote} isDisabled={saving || !noteBody.trim()}>Add note</Button>
        </Box>
      </VStack>
    </Box>
  );
}
